import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore } from '../../stores/editorStore';
import {
  getAliasesForDecoration,
  getAliasesForEntity,
  normalizeAssetKey,
  resolveDecorationImage,
  resolveEntityImage,
} from '../../utils/assetPreview';

type AtlasFrameMeta = {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
};

type AtlasData = {
  frames: Record<string, AtlasFrameMeta>;
};

type AnimationEntry = {
  key: string;
  frames: string[];
  frameRate: number;
  repeat: number;
};

type EntityAnimationData = {
  texture: string;
  animations: AnimationEntry[];
};

type AnimationsData = {
  entities: Record<string, EntityAnimationData>;
};

type AtlasAnimation = {
  entityKey: string;
  textureKey: string;
  animation: AnimationEntry;
};

type SpriteFrameItem = {
  textureKey: string;
  frameName: string;
  frame: AtlasFrameMeta;
};

let animationsDataPromise: Promise<AnimationsData | null> | null = null;
const atlasDataPromiseCache = new Map<string, Promise<AtlasData | null>>();

function getAnimationsData(): Promise<AnimationsData | null> {
  if (!animationsDataPromise) {
    animationsDataPromise = fetch('/assets/data/animations-c3data.json')
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json()) as AnimationsData;
      })
      .catch(() => null);
  }

  return animationsDataPromise;
}

function getAtlasData(textureKey: string): Promise<AtlasData | null> {
  const normalized = normalizeAssetKey(textureKey);
  const existing = atlasDataPromiseCache.get(normalized);
  if (existing) return existing;

  const promise = fetch(`/assets/game/spritesheets/${normalized}.json`)
    .then(async (res) => {
      if (!res.ok) return null;
      return (await res.json()) as AtlasData;
    })
    .catch(() => null);

  atlasDataPromiseCache.set(normalized, promise);
  return promise;
}

function getStringProp(source: unknown, key: string): string | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function getFrameSortValue(name: string): { prefix: string; index: number } {
  const match = name.match(/^(.*?)(?:_(\d+))?$/);
  return {
    prefix: match?.[1] ?? name,
    index: match?.[2] ? Number(match[2]) : Number.MAX_SAFE_INTEGER,
  };
}

function AtlasFrameCanvas({
  textureKey,
  frame,
  frameName,
  size,
}: {
  textureKey: string;
  frame: AtlasFrameMeta;
  frameName: string;
  size: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    const image = new Image();

    image.onload = () => {
      if (cancelled || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const srcSize = frame.sourceSize ?? { w: frame.frame.w, h: frame.frame.h };
      const srcPos = frame.spriteSourceSize ?? { x: 0, y: 0, w: frame.frame.w, h: frame.frame.h };

      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, size, size);

      const scale = Math.min((size - 8) / srcSize.w, (size - 8) / srcSize.h);
      const drawW = srcSize.w * scale;
      const drawH = srcSize.h * scale;
      const offsetX = (size - drawW) / 2;
      const offsetY = (size - drawH) / 2;

      ctx.drawImage(
        image,
        frame.frame.x,
        frame.frame.y,
        frame.frame.w,
        frame.frame.h,
        offsetX + srcPos.x * scale,
        offsetY + srcPos.y * scale,
        srcPos.w * scale,
        srcPos.h * scale
      );
    };

    image.src = `/assets/game/spritesheets/${normalizeAssetKey(textureKey)}.png`;

    return () => {
      cancelled = true;
    };
  }, [frame, frameName, size, textureKey]);

  return <canvas ref={canvasRef} width={size} height={size} />;
}

function AnimationPlayer({
  textureKey,
  animation,
  atlas,
}: {
  textureKey: string;
  animation: AnimationEntry;
  atlas: AtlasData;
}) {
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    setFrameIndex(0);
  }, [animation.key, textureKey]);

  useEffect(() => {
    const frameCount = animation.frames.length;
    if (frameCount <= 1) return;

    const fps = Math.max(1, animation.frameRate || 10);
    const ms = Math.max(40, Math.floor(1000 / fps));
    const interval = window.setInterval(() => {
      setFrameIndex((prev) => (prev + 1) % frameCount);
    }, ms);

    return () => window.clearInterval(interval);
  }, [animation.frameRate, animation.frames]);

  const currentFrameName = animation.frames[frameIndex] ?? animation.frames[0];
  const currentFrame = currentFrameName ? atlas.frames[currentFrameName] : undefined;

  if (!currentFrame || !currentFrameName) {
    return <div className="anim-empty">Animación sin frames en atlas.</div>;
  }

  return (
    <div className="anim-preview-row">
      <div className="anim-preview-visual">
        <AtlasFrameCanvas
          textureKey={textureKey}
          frame={currentFrame}
          frameName={currentFrameName}
          size={160}
        />
      </div>
      <div className="anim-preview-meta">
        <strong>{animation.key}</strong>
        <span>{animation.frames.length} frames | {animation.frameRate} fps</span>
        <span>Frame actual: {currentFrameName}</span>
      </div>
    </div>
  );
}

type ActiveSelection = {
  source: 'canvas' | 'palette';
  kind: 'layout' | 'entity';
  type: string;
  width?: number | undefined;
  height?: number | undefined;
  animation?: string | undefined;
};

export default function SpriteInspectorPanel() {
  const { selectedIds, objects, entities, paletteInspectorSelection } = useEditorStore();
  const [animationsData, setAnimationsData] = useState<AnimationsData | null>(null);
  const [atlasByTexture, setAtlasByTexture] = useState<Record<string, AtlasData>>({});
  const [isLoadingAtlases, setIsLoadingAtlases] = useState(false);

  useEffect(() => {
    let active = true;
    getAnimationsData().then((data) => {
      if (!active) return;
      setAnimationsData(data);
    });

    return () => {
      active = false;
    };
  }, []);

  const activeSelection: ActiveSelection | null = useMemo(() => {
    if (selectedIds.length === 1) {
      const selectedId = selectedIds[0];
      const selectedObject = objects.find((obj) => obj.id === selectedId);
      if (selectedObject) {
        return {
          source: 'canvas',
          kind: 'layout',
          type: selectedObject.data.type,
          width: selectedObject.data.width,
          height: selectedObject.data.height,
          animation: selectedObject.data.animation,
        };
      }

      const selectedEntity = entities.find((entity) => entity.id === selectedId);
      if (selectedEntity) {
        const entityAnim = getStringProp(selectedEntity.data.properties, 'animation')
          ?? getStringProp(selectedEntity.data.properties, 'anim');

        return {
          source: 'canvas',
          kind: 'entity',
          type: selectedEntity.data.texture || selectedEntity.data.entityId,
          width: selectedEntity.data.width,
          height: selectedEntity.data.height,
          animation: entityAnim,
        };
      }
    }

    if (!paletteInspectorSelection) return null;

    return {
      source: 'palette',
      kind: paletteInspectorSelection.kind,
      type: paletteInspectorSelection.type,
      width: paletteInspectorSelection.width,
      height: paletteInspectorSelection.height,
      animation: paletteInspectorSelection.animation,
    };
  }, [entities, objects, paletteInspectorSelection, selectedIds]);

  const spriteAliases = useMemo(() => {
    if (!activeSelection) return [];

    if (activeSelection.kind === 'entity') {
      return getAliasesForEntity(activeSelection.type).map((alias) => ({
        key: alias,
        image: resolveEntityImage(alias),
      }));
    }

    return getAliasesForDecoration(activeSelection.type).map((alias) => ({
      key: alias,
      image: resolveDecorationImage(alias),
    }));
  }, [activeSelection]);

  const candidateTextureKeys = useMemo(() => {
    if (!activeSelection) return [] as string[];

    const aliasSet = new Set<string>([
      normalizeAssetKey(activeSelection.type),
      ...spriteAliases.map((item) => normalizeAssetKey(item.key)),
    ]);

    const textures = new Set<string>(aliasSet);
    if (animationsData) {
      for (const [entityKey, data] of Object.entries(animationsData.entities)) {
        if (
          aliasSet.has(normalizeAssetKey(entityKey))
          || aliasSet.has(normalizeAssetKey(data.texture))
        ) {
          textures.add(normalizeAssetKey(data.texture));
        }
      }
    }

    return Array.from(textures).sort();
  }, [activeSelection, animationsData, spriteAliases]);

  useEffect(() => {
    if (candidateTextureKeys.length === 0) {
      setAtlasByTexture({});
      return;
    }

    let active = true;
    setIsLoadingAtlases(true);

    Promise.all(
      candidateTextureKeys.map(async (textureKey) => {
        const atlas = await getAtlasData(textureKey);
        return atlas ? [textureKey, atlas] as const : null;
      })
    ).then((results) => {
      if (!active) return;

      const next: Record<string, AtlasData> = {};
      for (const result of results) {
        if (!result) continue;
        next[result[0]] = result[1];
      }

      setAtlasByTexture(next);
      setIsLoadingAtlases(false);
    });

    return () => {
      active = false;
    };
  }, [candidateTextureKeys]);

  const relatedFrames = useMemo(() => {
    const entries: SpriteFrameItem[] = [];

    for (const [textureKey, atlas] of Object.entries(atlasByTexture)) {
      const sortedFrames = Object.entries(atlas.frames).sort(([a], [b]) => {
        const sa = getFrameSortValue(a);
        const sb = getFrameSortValue(b);
        if (sa.prefix !== sb.prefix) return sa.prefix.localeCompare(sb.prefix);
        if (sa.index !== sb.index) return sa.index - sb.index;
        return a.localeCompare(b);
      });

      for (const [frameName, frame] of sortedFrames) {
        entries.push({ textureKey, frameName, frame });
      }
    }

    return entries;
  }, [atlasByTexture]);

  const relatedAnimations = useMemo(() => {
    if (!animationsData || !activeSelection) return [] as AtlasAnimation[];

    const aliasSet = new Set<string>([
      normalizeAssetKey(activeSelection.type),
      ...spriteAliases.map((item) => normalizeAssetKey(item.key)),
    ]);

    const result: AtlasAnimation[] = [];
    for (const [entityKey, data] of Object.entries(animationsData.entities)) {
      const match =
        aliasSet.has(normalizeAssetKey(entityKey))
        || aliasSet.has(normalizeAssetKey(data.texture));

      if (!match) continue;

      for (const animation of data.animations) {
        result.push({
          entityKey,
          textureKey: normalizeAssetKey(data.texture),
          animation,
        });
      }
    }

    result.sort((a, b) => a.animation.key.localeCompare(b.animation.key));
    return result;
  }, [activeSelection, animationsData, spriteAliases]);

  const staticFallbackSprites = useMemo(() => {
    if (relatedFrames.length > 0) return [] as { key: string; image: string }[];
    return spriteAliases;
  }, [relatedFrames.length, spriteAliases]);

  if (!activeSelection) {
    return (
      <div className="sprite-inspector-panel">
        <div className="sprite-inspector-empty">
          Haz clic en una imagen del mapa o de la paleta para ver sus sprites y animaciones.
        </div>
      </div>
    );
  }

  return (
    <div className="sprite-inspector-panel">
      <div className="sprite-inspector-header">
        <div>
          <strong>{activeSelection.type}</strong>
          <span className="sprite-inspector-source">
            {activeSelection.source === 'canvas' ? 'Seleccionado en mapa' : 'Seleccionado en paleta'}
          </span>
        </div>
        {(activeSelection.width || activeSelection.height) && (
          <span className="sprite-inspector-size">
            {activeSelection.width ?? '-'} x {activeSelection.height ?? '-'}
          </span>
        )}
      </div>

      <div className="sprite-inspector-content">
        <div className="sprite-column">
          <div className="sprite-section-title">
            <h4>Sprites</h4>
            {relatedFrames.length > 0 && <span>{relatedFrames.length}</span>}
          </div>
          <div className="sprite-list">
            {isLoadingAtlases && <p className="anim-empty">Cargando sprites del atlas...</p>}

            {!isLoadingAtlases && relatedFrames.map((item, index) => (
              <div
                key={`${item.textureKey}:${item.frameName}`}
                className="sprite-list-item"
                title={`${item.textureKey} :: ${item.frameName}`}
              >
                <span className="sprite-order">#{index + 1}</span>
                <AtlasFrameCanvas
                  textureKey={item.textureKey}
                  frame={item.frame}
                  frameName={item.frameName}
                  size={70}
                />
                <span>{item.frameName}</span>
              </div>
            ))}

            {!isLoadingAtlases && relatedFrames.length === 0 && staticFallbackSprites.map((sprite, index) => (
              <div key={sprite.key} className="sprite-list-item" title={sprite.key}>
                <span className="sprite-order">#{index + 1}</span>
                <img
                  src={sprite.image}
                  alt={sprite.key}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden';
                  }}
                />
                <span>{sprite.key}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="anim-column">
          <div className="sprite-section-title">
            <h4>Animaciones</h4>
          </div>
          {relatedAnimations.length === 0 ? (
            <p className="anim-empty">No hay animaciones configuradas en animations-c3data para esta selección.</p>
          ) : (
            <div className="anim-preview-list">
              {relatedAnimations.map((entry) => {
                const atlas = atlasByTexture[entry.textureKey];
                if (!atlas) return null;

                return (
                  <AnimationPlayer
                    key={`${entry.entityKey}:${entry.animation.key}`}
                    textureKey={entry.textureKey}
                    animation={entry.animation}
                    atlas={atlas}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
